import * as turf from "@turf/turf";
import { Feature, FeatureCollection, MultiPolygon, Point, Polygon } from "geojson";
import { LngLat, LngLatBounds } from "mapbox-gl";
import IGeoCoordinate from "../interfaces/IGeoCoordinate";
import Shape from "../interfaces/Shape";

export default class GeometryService {
    public getShapeCenterPoint(shape: Shape): LngLat {        
        const polygon = this.mapIShapesToPolygonFeatures([shape])[0].geometry;
        return this.getPolygonCenterPoint(polygon);
    }

    public getPolygonCenterPoint(polygon: Polygon): LngLat {
        const point = turf.pointOnFeature(turf.polygon(polygon.coordinates));
        const [lng, lat] = point.geometry.coordinates;
        return new LngLat(lng, lat);
    }

    public isPointInsideShape(point: IGeoCoordinate, shape: Shape | Shape[]): boolean {
        return this.isPointInsideShapes(point, Array.isArray(shape) ? shape : [shape]);
    }

    public isPointInsideShapes(point: IGeoCoordinate, shapes: Shape[]): boolean {
        const validShapes = shapes.filter(shape => this.isValidShape(shape));
        if (!validShapes.length)
            return false;

        const turfPoint = turf.point([point.longitude, point.latitude]);
        return this.mapIShapesToPolygonFeatures(validShapes)
            .some(polygon => turf.booleanPointInPolygon(turfPoint, polygon, { ignoreBoundary: false }));
    }

    public mapPolygonToShape(polygon: Polygon): Shape {
        return {
            points: polygon.coordinates[0].map((ll: number[]) => {
                return { latitude: ll[1], longitude: ll[0] };
            }),
        };
    }

    public mapGeometryToShapes(geometry: Polygon | MultiPolygon): Shape[] {
        return geometry.type === "MultiPolygon"
            ? geometry.coordinates.map(coordinates => this.mapPolygonToShape({ type: "Polygon", coordinates }))
            : [this.mapPolygonToShape(geometry)];
    }

    public mapIShapesToPolygonFeatures(shapes: Shape[]): Feature<Polygon>[] {
        return shapes.map((shape: Shape): Feature<Polygon> => ({
            type: "Feature",
            properties: {},
            geometry: {
                type: "Polygon",
                coordinates: [this.toClosedLinearRing(shape.points)],
            },
        }));
    }

    public mapIShapesToMultiPolygonFeature(shapes: Shape[]): Feature<MultiPolygon> {
        return {
            type: "Feature",
            properties: {},
            geometry: {
                type: "MultiPolygon",
                coordinates: this.mapIShapesToPolygonFeatures(shapes)
                    .map(polygonFeature => [polygonFeature.geometry.coordinates[0]]),
            },
        };
    }

    public getBoundaryBox(feature: Feature<Point | Polygon | MultiPolygon> | FeatureCollection<any>): LngLatBounds {
        const bbox = turf.bbox(feature);
        return new LngLatBounds(new LngLat(bbox[0], bbox[1]), new LngLat(bbox[2], bbox[3]));
    }

    private isValidShape(shape: Shape): boolean {
        return shape.points.length >= 3;
    }

    private toClosedLinearRing(points: IGeoCoordinate[]): number[][] {
        const ring = points.map(point => [point.longitude, point.latitude]);
        if (ring.length < 2)
            return ring;

        const [firstLng, firstLat] = ring[0];
        const [lastLng, lastLat] = ring[ring.length - 1];
        if (firstLng !== lastLng || firstLat !== lastLat)
            ring.push([firstLng, firstLat]);

        return ring;
    }
}

export enum State {
    Maryland = "MD",
    Virginia = "VA",
    Washington = "DC",
    None = "None",
}
